import { getMerchant, updateMerchant } from 'APIs/dashboard/merchants';
import { UpdateUsageRules, UsageRules } from 'APIs/dashboard/merchants.types';
import Alert from 'Components/Alert/Alert';
import { Select } from 'Components/BankDetails/styles';
import Modal from 'Components/Modal/Modal';
import Spinner from 'Components/Spinner/Spinner';
import { H2, Input, Label, LpBox, LpDetails, P } from 'Constants/styles';
import { ChangeEvent, FormEvent, MouseEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import getCountryName from 'utils/getCountryName';
import setBodyOverflow from 'utils/setBodyOverflow';
import validateCurrencyCodes from 'utils/validateCurrencyCodes';

import { ChipRemove, CountryChip, EditBtn } from './styles';

type UsageRulesSettingsProps = {
  merchantId: string;
};

type FormDataType = {
  currency_allowed?: boolean;
  currency_list?: string;
  issuer_country_allowed?: boolean;
  issuer_country_list?: string;
};

const UsageRulesSettings = ({ merchantId }: UsageRulesSettingsProps): ReactElement => {
  const [existingUsageRules, setExistingUsageRules] = useState<UsageRules>({} as UsageRules);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [formData, setFormData] = useState<FormDataType>({
    currency_allowed: true,
    currency_list: '',
    issuer_country_allowed: true,
    issuer_country_list: '',
  });

  const fetchUsageRules = useCallback(async () => {
    if (!merchantId) {
      return;
    }
    setIsLoading(true);
    try {
      const merchant = await getMerchant(merchantId);
      setExistingUsageRules(merchant.usageRules);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setErrorMsg('Error fetching usage rules: ' + msg);
    } finally {
      setIsLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchUsageRules();
  }, [fetchUsageRules, merchantId]);

  useEffect(() => {
    if (!existingUsageRules) {
      return;
    }
    setFormData({
      currency_allowed: existingUsageRules.currency
        ? 'CurrencyRestrictionDisallow' in existingUsageRules.currency
          ? false
          : true
        : true,
      currency_list: existingUsageRules.currency
        ? 'CurrencyRestrictionDisallow' in existingUsageRules.currency
          ? existingUsageRules.currency.CurrencyRestrictionDisallow?.disallow.join(', ') ?? ''
          : existingUsageRules.currency.CurrencyRestrictionAllow?.allow.join(', ') ?? ''
        : '',
      issuer_country_allowed: existingUsageRules.issuerCountry
        ? 'IssuerCountryRestrictionDisallow' in existingUsageRules.issuerCountry
          ? false
          : true
        : true,
      issuer_country_list: existingUsageRules.issuerCountry
        ? 'IssuerCountryRestrictionDisallow' in existingUsageRules.issuerCountry
          ? existingUsageRules.issuerCountry.IssuerCountryRestrictionDisallow?.disallow.join(', ') ?? ''
          : existingUsageRules.issuerCountry.IssuerCountryRestrictionAllow?.allow.join(', ') ?? ''
        : '',
    });
  }, [existingUsageRules]);

  const toggleModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setBodyOverflow('hidden');
    setShowModal(true);
    setModalErrorMsg('');
    setModalIsLoading(false);
  };

  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setBodyOverflow('auto');
    setShowModal(false);
  };

  const countryList = ['AU', 'HK', 'NZ', 'SG', 'ZA', 'US'];

  const validateForm = useCallback((): string => {
    const currencies = formData.currency_list
      ? formData.currency_list
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean)
      : [];
    return validateCurrencyCodes(currencies);
  }, [formData.currency_list]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();

      setModalIsLoading(true);
      setModalErrorMsg('');

      const validationError = validateForm();
      if (validationError) {
        setModalErrorMsg(validationError);
        setModalIsLoading(false);
        return;
      }

      try {
        const currencyList =
          formData.currency_list
            ?.split(',')
            .map((c) => c.trim())
            .filter(Boolean) ?? [];
        const countryList = formData.issuer_country_list?.split(',').map((c) => c.trim()) ?? [];

        const payload = {
          UpdateUsageRules: {
            currency: formData.currency_list
              ? {
                  CurrencyRestrictionAllow: formData.currency_allowed
                    ? {
                        allow: currencyList,
                      }
                    : undefined,
                  CurrencyRestrictionDisallow: !formData.currency_allowed
                    ? {
                        disallow: currencyList,
                      }
                    : undefined,
                }
              : undefined,
            issuerCountry: formData.issuer_country_list
              ? {
                  IssuerCountryRestrictionAllow: formData.issuer_country_allowed
                    ? {
                        allow: countryList,
                      }
                    : undefined,
                  IssuerCountryRestrictionDisallow: !formData.issuer_country_allowed
                    ? {
                        disallow: countryList,
                      }
                    : undefined,
                }
              : undefined,
          },
        } as UpdateUsageRules;
        const resp = await updateMerchant(merchantId, payload);
        setExistingUsageRules(resp.usageRules);
        setShowModal(false);
        setBodyOverflow('auto');
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        setModalErrorMsg(`Error: ${msg}`);
      } finally {
        setModalIsLoading(false);
      }
    },
    [
      formData.currency_allowed,
      formData.currency_list,
      formData.issuer_country_allowed,
      formData.issuer_country_list,
      merchantId,
      validateForm,
    ],
  );

  const handleCurrencyListChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value.toUpperCase(),
    }));
  };

  const handleAllowSelectChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setFormData((prev) => {
      return {
        ...prev,
        [e.target.name]: e.target.value === 'allow' ? true : false,
      };
    });
  };

  const parseCountryList = (list?: string): string[] =>
    list
      ? list
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean)
      : [];

  const handleCountrySelectChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const code = e.target.value;
    if (!code) {
      return;
    }
    setFormData((prev) => {
      const selected = parseCountryList(prev.issuer_country_list);
      if (selected.includes(code)) {
        return prev;
      }
      return {
        ...prev,
        issuer_country_list: [...selected, code].join(', '),
      };
    });
  };

  const removeCountry = (code: string): void => {
    setFormData((prev) => ({
      ...prev,
      issuer_country_list: parseCountryList(prev.issuer_country_list)
        .filter((c) => c !== code)
        .join(', '),
    }));
  };

  return (
    <LpBox>
      <div className="row">
        <div className="col-sm-7">
          <H2 className="mb-0" data-testid="UsageRules">
            Usage rules
          </H2>
          <P className="mb-0 mb-md-3">
            Restrictions on which currencies, card-issuer countries are accepted or blocked.
          </P>
        </div>
        <div className="col-sm-5 text-left text-md-right text-sm-right text-xl-right">
          {errorMsg.length === 0 && (
            <EditBtn onClick={toggleModal}>
              {!Boolean(existingUsageRules) || (!existingUsageRules.currency && !existingUsageRules.issuerCountry)
                ? 'Add'
                : 'Edit'}{' '}
              rules
            </EditBtn>
          )}
        </div>
      </div>
      {errorMsg.length > 0 && <Alert message={errorMsg} />}
      {isLoading && (
        <div className="text-center">
          <Spinner />
        </div>
      )}
      {!isLoading && errorMsg.length === 0 && (
        <>
          {existingUsageRules.currency && (
            <LpDetails>
              {'CurrencyRestrictionAllow' in existingUsageRules.currency && (
                <div className="row pt-4">
                  <div className="col-sm-4 col-xl-3">
                    <Label>Allowed currencies</Label>
                  </div>
                  <div className="col-sm-8 col-xl-9" data-testid="business-info-taxId">
                    {existingUsageRules.currency.CurrencyRestrictionAllow?.allow.join(', ')}
                  </div>
                </div>
              )}
              {'CurrencyRestrictionDisallow' in existingUsageRules.currency && (
                <div className="row pt-4">
                  <div className="col-sm-4 col-xl-3">
                    <Label>Disallowed currencies</Label>
                  </div>
                  <div className="col-sm-8 col-xl-9" data-testid="business-info-taxId">
                    {existingUsageRules.currency.CurrencyRestrictionDisallow?.disallow.join(', ')}
                  </div>
                </div>
              )}
            </LpDetails>
          )}
          {existingUsageRules.issuerCountry && (
            <LpDetails>
              {'IssuerCountryRestrictionAllow' in existingUsageRules.issuerCountry && (
                <div className="row pt-4">
                  <div className="col-sm-4 col-xl-3">
                    <Label>Allowed issuer countries</Label>
                  </div>
                  <div className="col-sm-8 col-xl-9" data-testid="business-info-taxId">
                    {existingUsageRules.issuerCountry.IssuerCountryRestrictionAllow?.allow
                      .map((cc) => getCountryName(cc))
                      .join(', ')}
                  </div>
                </div>
              )}
              {'IssuerCountryRestrictionDisallow' in existingUsageRules.issuerCountry && (
                <div className="row pt-4">
                  <div className="col-sm-4 col-xl-3">
                    <Label>Disallowed issuer countries</Label>
                  </div>
                  <div className="col-sm-8 col-xl-9" data-testid="business-info-taxId">
                    {existingUsageRules.issuerCountry.IssuerCountryRestrictionDisallow?.disallow
                      .map((cc) => getCountryName(cc))
                      .join(', ')}
                  </div>
                </div>
              )}
            </LpDetails>
          )}
        </>
      )}
      {showModal && (
        <Modal
          title="Edit usage rules"
          cancelBtnText="Cancel"
          confirmBtnText="Save changes"
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          isLoading={modalIsLoading}
          disableConfirmBtn={modalIsLoading}
        >
          {modalIsLoading && modalErrorMsg.length === 0 && (
            <div className="text-center">
              <Spinner />
            </div>
          )}
          {modalErrorMsg.length > 0 && <Alert message={modalErrorMsg} />}
          {!modalIsLoading && (
            <div className="lp-details pt-2 pb-0">
              <div className="form-item">
                <div className="row mt-4 align-items-start">
                  <div className="col-md-3">
                    <Label>Currencies</Label>
                  </div>
                  <div className="col-md-3">
                    <Select
                      data-testid="currency-allow-select"
                      onChange={handleAllowSelectChange}
                      name="currency_allowed"
                      value={formData.currency_allowed ? 'allow' : 'disallow'}
                    >
                      <option
                        data-testid={`currency-select-allow`}
                        className="customer-select-option"
                        value="allow"
                        key="allow"
                      >
                        Allow
                      </option>
                      <option
                        data-testid={`currency-select-disallow`}
                        className="customer-select-option"
                        value="disallow"
                        key="disallow"
                      >
                        Disallow
                      </option>
                    </Select>
                  </div>
                  <div className="col-md-6">
                    <Input
                      type="text"
                      name="currency_list"
                      data-testid={`currency-list-input`}
                      value={formData.currency_list ?? ''}
                      placeholder="Currency list"
                      onChange={handleCurrencyListChange}
                    />
                  </div>
                </div>
                <div className="row mt-1 align-items-start">
                  <div className="col-md-3">
                    <Label>Issuer countries</Label>
                  </div>
                  <div className="col-md-3">
                    <Select
                      data-testid="issuer-country-select"
                      onChange={handleAllowSelectChange}
                      name="issuer_country_allowed"
                      value={formData.issuer_country_allowed ? 'allow' : 'disallow'}
                    >
                      <option
                        data-testid={`issuer-country-select-allow`}
                        className="customer-select-option"
                        value="allow"
                        key="allow"
                      >
                        Allow
                      </option>
                      <option
                        data-testid={`issuer-country-select-disallow`}
                        className="customer-select-option"
                        value="disallow"
                        key="disallow"
                      >
                        Disallow
                      </option>
                    </Select>
                  </div>
                  <div className="col-md-6">
                    <Select
                      data-testid="issuer-country-list-select"
                      onChange={handleCountrySelectChange}
                      name="issuer_country_list"
                      value=""
                    >
                      <option value="" key="placeholder">
                        Add a country
                      </option>
                      {countryList
                        .filter((country) => !parseCountryList(formData.issuer_country_list).includes(country))
                        .map((country) => (
                          <option className="customer-select-option" value={country} key={country}>
                            {getCountryName(country)}
                          </option>
                        ))}
                    </Select>
                    <div className="d-flex flex-wrap mt-2">
                      {parseCountryList(formData.issuer_country_list).map((country) => (
                        <CountryChip data-testid={`issuer-country-chip-${country}`} key={country}>
                          {getCountryName(country)}
                          <ChipRemove
                            type="button"
                            aria-label={`Remove ${getCountryName(country)}`}
                            onClick={() => removeCountry(country)}
                          >
                            ×
                          </ChipRemove>
                        </CountryChip>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}
    </LpBox>
  );
};

export default UsageRulesSettings;
